<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const usuariosQuery = gql`
  query usuariosSelect($pastaCodigo: String!) {
    data: responsaveisPorPasta(pastaCodigo: $pastaCodigo) {
      value: codigo
      label: nome
    }
  }
`;

const mostrarHistoricoSolicitacoes = ref('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];

function disabledDate(current: any) {
  return (
    current.day() === 0 ||
    current.day() === 6 ||
    current <= dayjs().subtract(1, 'day').endOf('day')
  );
}

const pastaCodigo = computed(() => {
  if (props.pastaCodigos && props.pastaCodigos.length > 0) {
    return props.pastaCodigos![0];
  } else {
    return props.compromisso?.fluxo?.pastaCodigo;
  }
});

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});
</script>

<template>
  <CompromissoForm
    action="compromisso-responsavel/criar-compromisso-responsavel"
    :pasta-codigos="props.pastaCodigos ?? []"
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="true"
    :submit-button-disabled="buttonsDisabled"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:active-key="mostrarHistoricoSolicitacoes"
        style="margin-bottom: 24px"
      >
        <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 74,
                },
                fluxo: {
                  pastaCodigo: {
                    in: props.pastaCodigos,
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>

      <a-form-item
        label="Responsável"
        name="responsavel"
        :rules="[
          {
            message: 'Responsável é obrigatório',
            required: true,
            trigger: 'change',
            type: 'object',
          },
        ]"
      >
        <GqlSelect
          v-model:value="model.responsavel"
          :query="usuariosQuery"
          :variables="{ pastaCodigo }"
          placeholder="Selecione responsável"
          :allow-clear="true"
        />
      </a-form-item>

      <a-form-item
        label="Descrição"
        name="descricao"
        :rules="[
          {
            message: 'Descrição é obrigatória',
            required: true,
          },
        ]"
        style="padding-bottom: 0"
      >
        <a-textarea v-model:value="model.descricao" :rows="4" />
      </a-form-item>

      <a-form-item
        label="Prazo"
        name="dataPrazo"
        :rules="[{ message: 'Prazo é obrigatório', required: true }]"
      >
        <a-date-picker
          v-model:value="model.dataPrazo"
          format="DD/MM/YYYY HH:mm"
          value-format="DD/MM/YYYY HH:mm"
          placeholder="Selecione uma data"
          style="width: 100%"
          :disabled-date="disabledDate"
          :show-time="{ defaultValue: dayjs('17:00:00', 'HH:mm:ss') }"
        />
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
